/* aviaf — login screen + user menu (wired to aviaf-control hub auth) */
const { useState: useAuth, useEffect: useAuthE, useRef: useAuthR } = React;

window.LoginScreen = function LoginScreen({ tweaks, onAuth, cycleTheme }) {
  const [user, setUser] = useAuth("");
  const [pw, setPw] = useAuth("");
  const [code, setCode] = useAuth("");
  const [challengeId, setChallengeId] = useAuth("");
  const [err, setErr] = useAuth("");
  const [busy, setBusy] = useAuth(false);
  const userRef = useAuthR(null);
  const codeRef = useAuthR(null);

  useAuthE(() => { userRef.current && userRef.current.focus(); }, []);
  useAuthE(() => {
    if (challengeId && codeRef.current) codeRef.current.focus();
  }, [challengeId]);

  const submit = async (e) => {
    e && e.preventDefault();
    if (!user) { setErr("username required"); return; }
    if (!pw) { setErr("auth key required"); return; }
    setErr(""); setBusy(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username: user, password: pw }),
      });
      if (res.status === 429) { setErr("rate limited · back off"); return; }
      if (res.status === 503) { setErr("2fa code delivery failed · check config"); return; }
      if (!res.ok) { setErr("auth rejected · check credentials"); return; }
      const body = await res.json();
      if (body.challenge_required && body.challenge_id) {
        setChallengeId(body.challenge_id);
        setErr("");
        return;
      }
      onAuth(body);
    } catch (ex) {
      setErr("network error · " + (ex.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e && e.preventDefault();
    if (!code) { setErr("code required"); return; }
    setErr(""); setBusy(true);
    try {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ challenge_id: challengeId, code }),
      });
      if (res.status === 410) {
        setErr("code expired · start over");
        setChallengeId(""); setCode(""); setPw("");
        return;
      }
      if (res.status === 429) {
        setErr("too many wrong codes · start over");
        setChallengeId(""); setCode(""); setPw("");
        return;
      }
      if (!res.ok) { setErr("invalid code"); return; }
      const body = await res.json();
      onAuth(body);
    } catch (ex) {
      setErr("network error · " + (ex.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1200 800">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.18" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          <polyline fill="none" stroke="currentColor" strokeWidth="1" opacity="0.35"
            points={Array.from({length:60}).map((_,i)=>{
              const x = i*20; let y = 600 - Math.sin(i*0.3)*40 - i*4 - (i%7)*8;
              return `${x},${Math.max(80,y)}`;
            }).join(" ")}
          />
          {Array.from({length:12}).map((_,i)=>(
            <line key={i} x1={i*100} y1={760} x2={i*100} y2={770} stroke="currentColor" opacity="0.4" />
          ))}
        </svg>
      </div>

      <button className="login-theme" onClick={cycleTheme} title="Cycle theme">
        {tweaks.theme==="paper" ? "☼ paper" : tweaks.theme==="dark" ? "☾ dark" : "▮ term"}
      </button>

      <div className="login-card">
        <div className="login-brand">
          <span className="brand-mark" />
          <div>
            <div className="login-mark">aviaf</div>
          </div>
        </div>

        {!challengeId ? (
          <form className="login-form" onSubmit={submit}>
            <label className="login-field">
              <span>Username</span>
              <input ref={userRef} type="text" value={user}
                     onChange={e=>setUser(e.target.value)}
                     placeholder="user" autoComplete="username"
                     autoCapitalize="off" autoCorrect="off" spellCheck={false} />
            </label>
            <label className="login-field">
              <span>Auth key</span>
              <input type="password" value={pw}
                     onChange={e=>setPw(e.target.value)}
                     placeholder="••••••••" autoComplete="current-password" />
            </label>
            {err && <div className="login-err">⨯ {err}</div>}
            <button type="submit" className="login-go" disabled={busy || !user || !pw}>
              {busy ? "authenticating…" : "authenticate →"}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={submitCode}>
            <label className="login-field">
              <span>2FA code</span>
              <input ref={codeRef} type="text" value={code}
                     inputMode="numeric" pattern="[0-9]*" maxLength={6}
                     onChange={e=>setCode(e.target.value.replace(/\D/g, ""))}
                     placeholder="••••••" autoComplete="one-time-code" />
            </label>
            {err && <div className="login-err">⨯ {err}</div>}
            <button type="submit" className="login-go" disabled={busy || !code}>
              {busy ? "verifying…" : "verify →"}
            </button>
            <button type="button" className="login-go" style={{opacity:0.6}}
                    onClick={()=>{setChallengeId(""); setCode(""); setErr("");}}>
              ← back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

window.UserMenu = function UserMenu({ session, onLogout }) {
  const [open, setOpen] = useAuth(false);
  const ref = useAuthR(null);
  useAuthE(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  // Hide entirely when auth is disabled (dev mode) — there's nothing to log out of.
  if (!session || session.auth_enabled === false) return null;
  const initial = "O";
  const subject = session.subject || "operator";
  return (
    <div className="user-menu" ref={ref}>
      <button className="user-chip" onClick={()=>setOpen(o=>!o)} data-open={open}>
        <span className="avatar">{initial}</span>
        <span className="uname">{subject}</span>
        <span className="muted" style={{fontSize:10}}>▾</span>
      </button>
      {open && (
        <div className="user-pop">
          <div className="up-head">
            <span className="avatar lg">{initial}</span>
            <div>
              <div className="up-name">{subject}</div>
              <div className="up-sub mono">operator</div>
            </div>
          </div>
          <button className="up-row" onClick={()=>{onLogout();setOpen(false);}}>
            <span style={{color:"var(--neg)"}}>Log out</span>
            <span className="muted mono">⇧L</span>
          </button>
        </div>
      )}
    </div>
  );
};
